/** @format */
import { Component, Input, OnInit, OnDestroy, inject, computed } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, take } from 'rxjs/operators';

import {
	BreakpointsService,
	KillswitchService,
	UtilityService
} from '@shared/data-access/services';

import {
	AppProductReviewState,
	InvalidResponse,
	ProductReviewAnalyticsService,
	addSearchFilter,
	removeReviewFilter,
	removeSearchFilter,
	selectProductReviewDrawer,
	selectSearchFilter,
	selectSelectedTag,
	BVPixelResponse,
	selectBVPixel,
	setSelectedFeature
} from '@product-reviews/data-access';

import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct
} from '../models';
import { chain } from 'chains/chain';
import { BreakPointNew } from '@shared/data-access/enums';

@Component({
	selector: 'product-reviews-search-input',
	templateUrl: './review-search-input.component.html',
	styleUrl: './review-search-input.component.scss',
	standalone: false
})
export class ReviewSearchInputComponent implements OnInit, OnDestroy {
	@Input() placeholder = '';

	private store = inject(Store<AppProductReviewState>);
	private analyticsService = inject(ProductReviewAnalyticsService);
	private utilityService = inject(UtilityService);
	private killSwitchService = inject(KillswitchService);
	private readonly breakpointsService = inject(BreakpointsService);

	searchFieldValue: string;
	isMobileSize = computed(() => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM);
	hasSearched = false;
	isFocus = false;
	hasNewReviewsEnhancement = false;
	selectedTagId = '';
	storeSelectedTag$ = this.store.select(selectSelectedTag);
	storeSelectorDrawer$ = this.store.select(selectProductReviewDrawer);
	storeSelectorSearch$ = this.store.select(selectSearchFilter);
	private UNSUBSCRIBE$ = new Subject<void>();
	selectBV$ = this.store.select(selectBVPixel);
	bvPixelResponse: BVPixelResponse;
	newReviewsEnhancement = false;

	ngOnInit(): void {
		this.handleKillSwitch();
		this.setupDrawerStore();
		this.setupSelectedTagStore();
		this.setupSearchStore();
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(take(1))
			.subscribe((data) => {
				this.newReviewsEnhancement = this.utilityService.checkKillSwitch(
					'newReviewsEnhancement',
					data,
					chain.chainIdentifierAbbr
				);
			});

		this.selectBV$.pipe(take(1)).subscribe((bv: BVPixelResponse) => {
			this.bvPixelResponse = bv;
		});
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	searchValue(): void {
		if (!this.searchFieldValue) {
			this.store.dispatch(removeSearchFilter());
			this.hasSearched = false;
			return;
		}

		if (this.selectedTagId) {
			this.store.dispatch(removeReviewFilter({ filterId: this.selectedTagId }));
		}

		this.store.dispatch(setSelectedFeature({ selectedFeature: null }));
		this.store.dispatch(addSearchFilter({ search: this.searchFieldValue }));
		this.hasSearched = true;

		this.analyticsService.fireReviewAnalytics<{
			details: string;
			event: string;
			value: string;
		}>({
			name: 'multiUseAnalyticsEvent',
			payload: {
				event: 'RatingsAndReviewsInteraction',
				value: 'Search',
				details: this.searchFieldValue
			}
		});

		if (this.newReviewsEnhancement) {
			const bvObject: BVEventObject = {
				type: BVEventObjectType.USED,
				name: BVEventObjectName.SEARCH,
				brand: this.bvPixelResponse.brand,
				productId: this.bvPixelResponse.productId,
				bvProduct: BVProduct.RATINGSANDREVIEWS,
				categoryId: this.bvPixelResponse.category,
				detail1: this.searchFieldValue,
				detail2: ''
			};

			BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);
		}
	}

	setFocus(focus: boolean): void {
		if (this.searchFieldValue) {
			return;
		}

		if (this.hasNewReviewsEnhancement && !this.searchFieldValue && !focus && this.hasSearched) {
			this.clearQuery();
			return;
		}

		this.isFocus = focus;
		this.hasSearched = false;
	}

	clearQuery(): void {
		if (this.searchFieldValue) {
			this.searchFieldValue = '';
		}

		this.isFocus = false;
		this.hasSearched = false;
		this.store.dispatch(removeSearchFilter());
	}

	private setupDrawerStore() {
		this.storeSelectorDrawer$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((data) => !(data instanceof InvalidResponse))
			)
			.subscribe(({ open }) => {
				if (open) {
					return;
				}

				if (this.searchFieldValue) {
					this.searchFieldValue = '';
				}

				this.isFocus = false;
				this.hasSearched = false;
			});
	}

	private setupSelectedTagStore() {
		this.storeSelectedTag$.pipe(takeUntil(this.UNSUBSCRIBE$)).subscribe((storeSelectedTag) => {
			this.selectedTagId =
				storeSelectedTag instanceof InvalidResponse ? '' : storeSelectedTag.id;
		});
	}

	private setupSearchStore(): void {
		this.storeSelectorSearch$.pipe(takeUntil(this.UNSUBSCRIBE$)).subscribe((searchStored) => {
			if (searchStored instanceof InvalidResponse) {
				this.searchFieldValue = '';
			}
		});
	}

	private handleKillSwitch(): void {
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				map((data) =>
					this.utilityService.checkKillSwitch(
						'newReviewsEnhancement',
						data,
						chain.chainIdentifierAbbr
					)
				)
			)
			.subscribe((hasNewReviewsEnhancement) => {
				this.hasNewReviewsEnhancement = hasNewReviewsEnhancement;
			});
	}
}
